import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Lightweight guest rating page — reached from the confirmation email.
 * Auth-free: the (orderId, token) tuple is the identity.
 */
export default function RateOrder() {
  const [params] = useSearchParams();
  const orderId = params.get("orderId") || "";
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!orderId || !token) {
        setErr("This rating link is missing its order details.");
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/rate-order/context", { params: { order_id: orderId, token } });
        if (!alive) return;
        setCtx(data);
        if (data?.already_rated) {
          setStars(data.stars || 0);
          setComment(data.comment || "");
          setSubmitted(true);
        }
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orderId, token]);

  const submit = async () => {
    if (!stars) { setErr("Please pick a star rating first."); return; }
    setSubmitting(true); setErr("");
    try {
      await api.post("/rate-order", { order_id: orderId, token, stars, comment: comment.trim() || undefined });
      setSubmitted(true);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7] flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-sm border border-slate-100 p-8" data-testid="rate-order-card">
        <div className="text-xs font-bold text-brandTeal tracking-widest uppercase mb-2">Leave a Review</div>
        <h1 className="text-2xl font-display font-semibold text-slate-900 mb-2">How was your handoff?</h1>
        {loading ? (
          <div className="py-8 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : err && !ctx ? (
          <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="rate-order-error">{err}</div>
        ) : (
          <>
            <p className="text-sm text-slate-600 leading-relaxed">
              {ctx?.item_title ? (<>Rate your experience buying <strong>{ctx.item_title}</strong> from <span className="text-brandTeal font-semibold">{ctx.seller_handle?.startsWith("@") ? ctx.seller_handle : `@${ctx.seller_handle || "seller"}`}</span>.</>) : "Rate your experience with the seller."}
            </p>
            {ctx?.order_ref && (
              <p className="text-xs text-slate-400 mt-1">Order Ref <strong className="text-slate-500">#{ctx.order_ref}</strong></p>
            )}

            <div className="flex items-center justify-center gap-2 my-6" data-testid="rate-order-stars">
              {[1,2,3,4,5].map((n) => {
                const active = (hover || stars) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    disabled={submitted}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setStars(n)}
                    className="p-1 disabled:opacity-70"
                    data-testid={`rate-order-star-${n}`}
                    aria-label={`${n} star${n === 1 ? "" : "s"}`}
                  >
                    <Star className={`w-9 h-9 transition ${active ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} strokeWidth={1.5} />
                  </button>
                );
              })}
            </div>

            <label htmlFor="rate-order-comment" className="text-xs font-semibold text-slate-700">Add a quick comment (optional)</label>
            <textarea
              id="rate-order-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={submitted}
              rows={3}
              maxLength={500}
              placeholder="What went well? Anything to flag?"
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/30 outline-none resize-none disabled:bg-slate-50"
              data-testid="rate-order-comment"
            />

            {err && <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="rate-order-inline-error">{err}</div>}

            {submitted ? (
              <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3" data-testid="rate-order-done">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <div className="text-sm text-emerald-800">Thanks for the {stars}-star review — it helps other families feel safe buying on MyTableTop.</div>
              </div>
            ) : (
              <button
                onClick={submit}
                disabled={submitting || !stars}
                className="btn-primary w-full mt-6 disabled:opacity-60"
                data-testid="rate-order-submit"
              >
                {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : "Submit rating"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
